'use client';

import React, { JSX, useCallback, useEffect, useMemo, useState } from 'react';

import { AutocompleteInput, AutocompleteProps, TextField, TextFieldProps } from 'lighthouse';

import { debounce } from 'lodash-es';

interface IPropTypes<Type> extends Omit<AutocompleteProps, 'renderInput' | 'options'> {
    getOptionLabel?: (option: any) => string;
    renderOption?: (option: any, state: any) => JSX.Element;
    onChange: (value: any) => void;
    setInputRef?: (input: HTMLInputElement) => void;
    onBlur?: () => void;
    api: (searchText: string) => Promise<Type[]> | [];
    id?: string;
    noOptionsText?: string | JSX.Element;
    label: string;
    className?: string;
    error?: boolean;
    helperText?: string;
    loadingText?: string;
    placeHolder?: string;
    defaultValue?: Type;
    defaultOption?: Array<Type>;
    debounceTimeout?: number;
    inputProps?: TextFieldProps;
    size?: AutocompleteProps['size'];
}

export default function AutocompleteSearchWithApi<Type>({
    getOptionLabel = (option) => option.title,
    renderOption,
    onChange,
    setInputRef,
    onBlur,
    api,
    id = 'AutocompleteWithSearch',
    noOptionsText = <span>چنین گزینه‌ای یافت نشد.‌</span>,
    label,
    error,
    helperText,
    placeHolder = 'جستجو ...',
    defaultValue,
    defaultOption = [],
    debounceTimeout = 500,
    inputProps = {},
    size = 'small',
    loading: isOptionsLoading = false,
    ...props
}: IPropTypes<Type>): JSX.Element {
    const [value, setValue] = useState<any>(null);
    const [searchValue, setSearchValue] = useState<string>('');
    const [options, setOptions] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [hasSearched, setHasSearched] = useState<boolean>(false);
    const [isInputFocused, setIsInputFocused] = useState<boolean>(false);
    const resolvedOptions = searchValue === '' ? defaultOption : options;
    const isLoading = loading || isOptionsLoading;
    const shouldOpenDropdown = isInputFocused && (isLoading || resolvedOptions.length > 0 || (hasSearched && !isLoading));

    const fetch = useMemo(
        () => async (input: string, callback: (results: Type[]) => void) => {
            callback(await api(input));
            setLoading(false);
        },

        []
    );

    const handleInputChange = useCallback(
        debounce((newInputValue: any) => {
            const trimmedInputValue = newInputValue.trim();

            setSearchValue(trimmedInputValue);
            setHasSearched(!!trimmedInputValue);

            if (!trimmedInputValue) {
                setLoading(false);
                return;
            }

            setLoading(true);
        }, debounceTimeout),
        []
    );

    const handleSelectChange = (newValue: Type) => {
        setOptions(newValue ? [...options, newValue] : options);
        setValue(newValue);
        setIsInputFocused(false);
        onChange(newValue);
    };

    useEffect(() => {
        defaultValue && setValue(defaultValue);
    }, [defaultValue]);

    useEffect(() => {
        let active = true;

        if (searchValue === '') {
            setOptions(defaultOption);
            setLoading(false);
            return undefined;
        }
        fetch(searchValue, (results?: Type[]) => {
            if (active) {
                let newOptions: any[] = [];
                if (value) newOptions = [value];

                if (results) newOptions = [...newOptions, ...results];

                setOptions(newOptions);
            }
        });

        return () => {
            active = false;
        };
    }, [value, searchValue, fetch]);

    return (
        <AutocompleteInput
            {...props}
            id={id}
            size={size}
            getOptionLabel={getOptionLabel}
            filterOptions={(x: any) => x}
            options={resolvedOptions}
            autoComplete
            includeInputInList
            filterSelectedOptions
            open={shouldOpenDropdown}
            noOptionsText={hasSearched && !isLoading ? noOptionsText : null}
            value={value}
            onChange={handleSelectChange}
            loading={isLoading}
            onInputChange={(event: any, newInputValue: any, reason: any) => {
                if (reason === 'input') {
                    setIsInputFocused(true);
                    handleInputChange(newInputValue);
                } else if (reason === 'clear') {
                    handleInputChange('');
                }
            }}
            renderInput={(params: any) => (
                <TextField
                    {...params}
                    inputRef={(input: any) => {
                        setInputRef && setInputRef(input);
                    }}
                    autoComplete='off'
                    name={inputProps?.name}
                    id={inputProps?.id}
                    variant='outlined'
                    label={label}
                    fullWidth
                    error={error}
                    helperText={helperText}
                    onFocus={() => setIsInputFocused(true)}
                    onBlur={() => {
                        setIsInputFocused(false);
                        onBlur?.();
                    }}
                    placeholder={placeHolder}
                />
            )}
            renderOption={renderOption}
        />
    );
}
